import * as React from "react";
import {
    AlertColor,
    Box,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
    ReportFormat,
    TimePeriod,
    AccessEventReportQueryParams,
    AccessEventUsageReportRequest,
    AccessEventUsageReportResponse,
} from "../../../../services/types/accessEventReportTypes";
import {
    useGetAccessEventTypesQuery,
    useGetAccessEventUsageReportMutation,
} from "../../../../services/cclTokenedAccessEventsApi";
import { useLazyGetGroupsByTenantIdQuery } from "../../../../services/cclTokenedGrandCentralApi";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import { FileDownloadService } from "../../../../services/fileDownloadService/fileDownloadService";
import {
    ChartComponent,
    SeriesCollectionDirective,
    SeriesDirective,
    Inject,
    Legend,
    Category,
    Tooltip,
    DataLabel,
    LineSeries,
} from "@syncfusion/ej2-react-charts";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";

const CCL_ACCESS_TENANT_ID = "7e8dbdc5-49cd-42b3-b856-1e9ff1fe41f1";

// Chart component for GroupEventTrend format (Groups → Events → Time periods)
const GroupEventTrendChart: React.FC<{ data: AccessEventUsageReportResponse }> = ({ data }) => {
    const reportData = data.reportData as any;
    const groups = reportData.groups || [];

    return (
        <Box>
            {groups.map((group: any, index: number) => {
                // Prepare chart data for this group
                const chartData = group.eventTypes.flatMap((eventType: any) =>
                    eventType.periods.map((period: any) => ({
                        x: formatUtcDate(period.startDate),
                        y: period.count,
                        eventType: eventType.eventTypeName,
                    })),
                );

                // Get unique event types for this group
                const eventTypeNames = [
                    ...new Set(chartData.map((d: any) => d.eventType)),
                ] as string[];

                return (
                    <Box key={group.groupId} sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                            {group.groupName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Total Events: {group.totalEvents} | Unique Users: {group.uniqueUsers}
                        </Typography>

                        <ChartComponent
                            id={`group-chart-${index}`}
                            primaryXAxis={{ valueType: "Category", title: "Time Period" }}
                            primaryYAxis={{ title: "Event Count" }}
                            title={`${group.groupName} - Event Trends`}
                            tooltip={{ enable: true }}
                            height="350px"
                        >
                            <Inject services={[LineSeries, Legend, Tooltip, Category, DataLabel]} />
                            <SeriesCollectionDirective>
                                {eventTypeNames.map((eventTypeName, seriesIndex) => (
                                    <SeriesDirective
                                        key={seriesIndex}
                                        dataSource={chartData.filter(
                                            (d: any) => d.eventType === eventTypeName,
                                        )}
                                        xName="x"
                                        yName="y"
                                        name={eventTypeName}
                                        type="Line"
                                        marker={{ visible: true, width: 10, height: 10 }}
                                    />
                                ))}
                            </SeriesCollectionDirective>
                        </ChartComponent>
                    </Box>
                );
            })}
        </Box>
    );
};

// Chart component for EventGroupTrend format (Events → Groups → Time periods)
const EventGroupTrendChart: React.FC<{ data: AccessEventUsageReportResponse }> = ({ data }) => {
    const reportData = data.reportData as any;
    const eventTypes = reportData.eventTypes || [];

    return (
        <Box>
            {eventTypes.map((eventType: any, index: number) => {
                // Prepare chart data for this event type
                const chartData = eventType.groups.flatMap((group: any) =>
                    group.periods.map((period: any) => ({
                        x: formatUtcDate(period.startDate),
                        y: period.count,
                        group: group.groupName,
                    })),
                );

                // Get unique groups for this event type
                const groupNames = [...new Set(chartData.map((d: any) => d.group))] as string[];

                return (
                    <Box key={eventType.eventTypeId} sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                            {eventType.eventTypeName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Total Count: {eventType.totalCount} | Unique Users:{" "}
                            {eventType.totalUniqueUsers}
                        </Typography>

                        <ChartComponent
                            id={`event-chart-${index}`}
                            primaryXAxis={{ valueType: "Category", title: "Time Period" }}
                            primaryYAxis={{ title: "Event Count" }}
                            title={`${eventType.eventTypeName} - Group Trends`}
                            tooltip={{ enable: true }}
                            height="350px"
                        >
                            <Inject services={[LineSeries, Legend, Tooltip, Category, DataLabel]} />
                            <SeriesCollectionDirective>
                                {groupNames.map((groupName, seriesIndex) => (
                                    <SeriesDirective
                                        key={seriesIndex}
                                        dataSource={chartData.filter(
                                            (d: any) => d.group === groupName,
                                        )}
                                        xName="x"
                                        yName="y"
                                        name={groupName}
                                        type="Line"
                                        marker={{ visible: true, width: 10, height: 10 }}
                                    />
                                ))}
                            </SeriesCollectionDirective>
                        </ChartComponent>
                    </Box>
                );
            })}
        </Box>
    );
};

// Chart component for EventTrendOverall format (Events → Time periods, overall)
const EventTrendOverallChart: React.FC<{ data: AccessEventUsageReportResponse }> = ({ data }) => {
    const reportData = data.reportData as any;
    const eventTypes = reportData.eventTypes || [];

    // Prepare chart data for all event types
    const chartData = eventTypes.flatMap((eventType: any) =>
        eventType.periods.map((period: any) => ({
            x: formatUtcDate(period.startDate),
            y: period.count,
            eventType: eventType.eventTypeName,
        })),
    );

    // Get unique event types
    const eventTypeNames = [...new Set(chartData.map((d: any) => d.eventType))] as string[];

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Events: {reportData.totalEvents} | Unique Users: {reportData.uniqueUsers}
            </Typography>

            <ChartComponent
                id="overall-chart"
                primaryXAxis={{ valueType: "Category", title: "Time Period" }}
                primaryYAxis={{ title: "Event Count" }}
                title="Overall Event Trends"
                tooltip={{ enable: true }}
                height="400px"
            >
                <Inject services={[LineSeries, Legend, Tooltip, Category, DataLabel]} />
                <SeriesCollectionDirective>
                    {eventTypeNames.map((eventTypeName, seriesIndex) => (
                        <SeriesDirective
                            key={seriesIndex}
                            dataSource={chartData.filter((d: any) => d.eventType === eventTypeName)}
                            xName="x"
                            yName="y"
                            name={eventTypeName}
                            type="Line"
                            marker={{ visible: true, width: 10, height: 10 }}
                        />
                    ))}
                </SeriesCollectionDirective>
            </ChartComponent>
        </Box>
    );
};

// The DatePicker gives us a Date representing local midnight of the selected calendar day.
// `.toISOString()` on that value converts local midnight to UTC, which shifts the instant (and
// can even roll into the adjacent UTC calendar day) depending on the browser's timezone offset.
// We want the backend to always see midnight UTC of the day the user picked, with no timezone
// conversion involved, so build the UTC timestamp directly from the picked date's Y/M/D instead.
const toUtcMidnightIso = (date: Date): string =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

// The inverse display concern: values coming back from the API (reportData.startDate/endDate,
// period.startDate) are UTC-midnight ISO timestamps for the calendar day they represent (see
// toUtcMidnightIso above). Formatting them with the browser's local timezone via a plain
// `.toLocaleDateString()` would shift the displayed day backward for anyone west of UTC - e.g.
// "2024-06-10T00:00:00Z" would read as 6/9/2024 in US time zones. Pinning the format to UTC
// keeps the displayed date matching the calendar day that was actually selected/queried.
const formatUtcDate = (isoString: string): string =>
    new Date(isoString).toLocaleDateString(undefined, { timeZone: "UTC" });

const ReportsPanel: React.FC = () => {
    // Neither date picker allows selecting a date later than today
    const maxSelectableDate = new Date();

    // Form state
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [format, setFormat] = React.useState<ReportFormat>(ReportFormat.GroupEventTrend);
    const [timePeriod, setTimePeriod] = React.useState<TimePeriod>(TimePeriod.None);
    const [selectedGroupIds, setSelectedGroupIds] = React.useState<string[]>([]);
    const [selectedEventTypeIds, setSelectedEventTypeIds] = React.useState<number[]>([]);

    // Validation errors
    const [dateError, setDateError] = React.useState<string>("");

    // Report data state
    const [reportData, setReportData] = React.useState<AccessEventUsageReportResponse | null>(null);

    // Bumped whenever a query/shape param changes (see the effect below). Captured locally at
    // the start of handleGenerateReport and re-checked when the request resolves, so a response
    // for params the user has since moved away from is discarded instead of overwriting the
    // (already-cleared) reportData with a chart that no longer matches the form.
    const requestGenerationRef = React.useRef(0);

    // Snackbar state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");

    // CSV export state
    const [isExportingCsv, setIsExportingCsv] = React.useState<boolean>(false);
    const downloadService = new FileDownloadService();

    // API hooks
    const { data: eventTypes } = useGetAccessEventTypesQuery();
    const [fetchGroups, { data: groups }] = useLazyGetGroupsByTenantIdQuery();
    const [generateReport, { isLoading: isGenerating }] = useGetAccessEventUsageReportMutation();

    // Fetch groups on mount
    React.useEffect(() => {
        fetchGroups(CCL_ACCESS_TENANT_ID);
    }, [fetchGroups]);

    // Hide stale results as soon as any query/shape parameter changes, so the chart shown
    // never gets out of sync with the current form state. Also invalidates any in-flight
    // generateReport request (see requestGenerationRef / handleGenerateReport).
    React.useEffect(() => {
        requestGenerationRef.current += 1;
        setReportData(null);
    }, [startDate, endDate, format, timePeriod, selectedGroupIds, selectedEventTypeIds]);

    const showSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const handleFormatChange = (event: SelectChangeEvent<number>) => {
        setFormat(event.target.value as number);
    };

    const handleTimePeriodChange = (event: SelectChangeEvent<number>) => {
        setTimePeriod(event.target.value as number);
    };

    const handleGroupIdsChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedGroupIds(typeof value === "string" ? value.split(",") : value);
    };

    const handleEventTypeIdsChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setSelectedEventTypeIds(typeof value === "string" ? [parseInt(value)] : value);
    };

    const validateForm = (): boolean => {
        setDateError("");

        if (!startDate || !endDate) {
            setDateError("Start date and end date are required");
            return false;
        }

        if (endDate < startDate) {
            setDateError("End date must be after start date");
            return false;
        }

        return true;
    };

    const buildQueryParams = (): AccessEventReportQueryParams => ({
        startDate: toUtcMidnightIso(startDate!),
        endDate: toUtcMidnightIso(endDate!),
        groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : null,
        eventTypeIds: selectedEventTypeIds.length > 0 ? selectedEventTypeIds : null,
    });

    const handleGenerateReport = async () => {
        if (!validateForm()) {
            showSnackbarElement("Please fix validation errors", "error");
            return;
        }

        // Snapshot the generation this request belongs to; if the user changes any query/shape
        // param before it resolves, the effect above bumps requestGenerationRef and this request
        // is treated as stale below rather than overwriting the form's current state.
        const requestGeneration = requestGenerationRef.current;

        try {
            const request: AccessEventUsageReportRequest = {
                query: buildQueryParams(),
                shape: { format, timePeriod },
            };

            const response = await generateReport(request).unwrap();

            if (requestGeneration !== requestGenerationRef.current) {
                return;
            }

            // Store report data for visualization
            setReportData(response);

            showSnackbarElement("Report Generated", "success");
        } catch (error: any) {
            console.error("Report generation error:", error);

            if (requestGeneration !== requestGenerationRef.current) {
                return;
            }

            showSnackbarElement("Report Generation Failed", "error");
        }
    };

    const handleDownloadCsv = async () => {
        if (!validateForm()) {
            showSnackbarElement("Please fix validation errors", "error");
            return;
        }

        setIsExportingCsv(true);
        try {
            await downloadService.DownloadAccessEventUsageExport(buildQueryParams());
            showSnackbarElement("CSV Export Completed", "success");
        } catch (error: any) {
            showSnackbarElement("CSV Export Failed", "error");
            console.error("Report export error:", error);
        } finally {
            setIsExportingCsv(false);
        }
    };

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
                Access Event Usage Reports
            </Typography>

            <Grid container spacing={3}>
                {/* Form Controls - always half width at md+ and full width below that,
                    regardless of whether results are currently shown */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                        {/* Query - defines which AccessEvent rows are in scope */}
                        <Box
                            component="fieldset"
                            sx={{
                                m: 0,
                                minWidth: 0,
                                p: 2,
                                pt: 1.5,
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                component="legend"
                                sx={{ px: 1, fontSize: "0.75rem", color: "text.secondary" }}
                            >
                                Query
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Start Date *"
                                        value={startDate}
                                        onChange={(newValue: Date | null) => setStartDate(newValue)}
                                        maxDate={maxSelectableDate}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                error={!!dateError}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="End Date *"
                                        value={endDate}
                                        onChange={(newValue: Date | null) => setEndDate(newValue)}
                                        maxDate={maxSelectableDate}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                error={!!dateError}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Event Type Filter */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="event-types-label">
                                            Event Types (Optional)
                                        </InputLabel>
                                        <Select
                                            size="small"
                                            labelId="event-types-label"
                                            multiple
                                            value={selectedEventTypeIds}
                                            label="Event Types (Optional)"
                                            onChange={handleEventTypeIdsChange}
                                            renderValue={(selected) =>
                                                selected
                                                    .map(
                                                        (id) =>
                                                            eventTypes?.find((et) => et.id === id)
                                                                ?.name || id.toString(),
                                                    )
                                                    .sort()
                                                    .join(", ")
                                            }
                                        >
                                            {eventTypes
                                                ?.slice()
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((eventType) => (
                                                    <MenuItem
                                                        key={eventType.id}
                                                        value={eventType.id}
                                                    >
                                                        {eventType.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* User Group Filter */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="groups-label">
                                            User Groups (Optional)
                                        </InputLabel>
                                        <Select
                                            size="small"
                                            labelId="groups-label"
                                            multiple
                                            value={selectedGroupIds}
                                            label="User Groups (Optional)"
                                            onChange={handleGroupIdsChange}
                                            renderValue={(selected) =>
                                                selected
                                                    .map(
                                                        (id) =>
                                                            groups?.find((g) => g.groupId === id)
                                                                ?.name || id,
                                                    )
                                                    .sort()
                                                    .join(", ")
                                            }
                                        >
                                            {groups
                                                ?.slice()
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((group) => (
                                                    <MenuItem
                                                        key={group.groupId}
                                                        value={group.groupId}
                                                    >
                                                        {group.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {dateError && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{dateError}</FormHelperText>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        {/* Shape - defines how the queried events get bucketed/displayed */}
                        <Box
                            component="fieldset"
                            sx={{
                                m: 0,
                                minWidth: 0,
                                p: 2,
                                pt: 1.5,
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                component="legend"
                                sx={{ px: 1, fontSize: "0.75rem", color: "text.secondary" }}
                            >
                                Shape
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="format-label">Report Format</InputLabel>
                                        <Select
                                            size="small"
                                            labelId="format-label"
                                            value={format}
                                            label="Report Format"
                                            onChange={handleFormatChange}
                                        >
                                            <MenuItem value={ReportFormat.GroupEventTrend}>
                                                Groups → Events → Time Periods
                                            </MenuItem>
                                            <MenuItem value={ReportFormat.EventGroupTrend}>
                                                Events → Groups → Time Periods
                                            </MenuItem>
                                            <MenuItem value={ReportFormat.EventTrendOverall}>
                                                Events → Time Periods (Overall)
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="time-period-label">Time Period</InputLabel>
                                        <Select
                                            size="small"
                                            labelId="time-period-label"
                                            value={timePeriod}
                                            label="Time Period"
                                            onChange={handleTimePeriodChange}
                                        >
                                            <MenuItem value={TimePeriod.None}>
                                                None (Single Period)
                                            </MenuItem>
                                            <MenuItem value={TimePeriod.Week}>Weekly</MenuItem>
                                            <MenuItem value={TimePeriod.Month}>Monthly</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Generate / Export Buttons */}
                        <Stack direction="row" spacing={2}>
                            <CclLoadingButton
                                mode="primary"
                                onClick={handleGenerateReport}
                                disabled={isGenerating}
                                size="small"
                                loading={isGenerating}
                            >
                                Generate Report
                            </CclLoadingButton>
                            <CclLoadingButton
                                mode="secondary"
                                onClick={handleDownloadCsv}
                                disabled={isExportingCsv}
                                size="small"
                                loading={isExportingCsv}
                            >
                                Download CSV
                            </CclLoadingButton>
                        </Stack>
                    </Stack>
                </Grid>

                {/* Results - half width alongside the form at md+, full width below it
                    on smaller screens; shown once a report is generated */}
                {reportData && (
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 3,
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {formatUtcDate(reportData.startDate)} -{" "}
                                {formatUtcDate(reportData.endDate)}
                            </Typography>

                            {reportData.formatId === ReportFormat.GroupEventTrend && (
                                <GroupEventTrendChart data={reportData} />
                            )}
                            {reportData.formatId === ReportFormat.EventGroupTrend && (
                                <EventGroupTrendChart data={reportData} />
                            )}
                            {reportData.formatId === ReportFormat.EventTrendOverall && (
                                <EventTrendOverallChart data={reportData} />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>

            {/* Snackbar for messages */}
            {showSnackbar && (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            )}
        </Box>
    );
};

export default ReportsPanel;
